/**
 * OFFER DETAIL (UF-06). The full terms of one offer, and the actions on it.
 *
 * Accepting an offer is the moment the collateral gets locked, so the page states that plainly before
 * the button. A borrower can also decline, and (once messaging exists) negotiate or ask questions; those
 * are shown as the intended actions rather than faked.
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { api, LipaApiError, type FinanceOffer } from '@/lib/api';
import { StatusPill } from '@/components/status';
import { ErrorState, LoadingCard } from '@/components/states';
import { Reveal } from '@/components/motion';

export default function OfferDetail() {
  // React Router types a param as possibly absent; this route cannot match without one.
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [offer, setOffer] = useState<FinanceOffer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'accept' | 'decline' | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setOffer(await api.finance.offer(id));
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not load this offer.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function accept() {
    setBusy('accept');
    try {
      await api.finance.acceptOffer(id);
      navigate('/credit/loans');
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not accept that offer.');
      setBusy(null);
    }
  }
  async function decline() {
    setBusy('decline');
    try {
      await api.finance.declineOffer(id);
      navigate('/credit/offers');
    } catch {
      setBusy(null);
    }
  }

  if (error) return <ErrorState body={error} onRetry={() => void load()} />;
  if (!offer) return <LoadingCard lines={4} />;

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <Link
        to="/credit/offers"
        className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
      >
        <BackIcon /> Offers
      </Link>

      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-page-title text-ink">{offer.provider}</h1>
          <p className="mt-0.5 text-sm text-ink-muted">Offer for {offer.asset}</p>
        </div>
        <StatusPill status={offer.status} />
      </header>

      <Reveal>
        <section className="rounded-card border border-border bg-surface p-5 shadow-card">
          <dl className="divide-y divide-border text-sm">
            <Row k="Amount" v={`KES ${offer.amount}`} strong />
            <Row k="Interest" v={`${offer.interestPct}% p.a.`} />
            <Row k="APR" v={`${offer.aprPct}%`} />
            <Row k="Max LTV" v={`${offer.maxLtvPct}%`} />
            <Row k="Tenure" v={`${offer.tenureMonths} months`} />
            <Row k="Fees" v={`KES ${offer.fees}`} />
            <Row k="Insurance" v={offer.insurance} />
            <Row k="Repayment" v={offer.repayment} />
            <Row k="Early settlement" v={offer.earlySettlement} />
          </dl>
        </section>
      </Reveal>

      {offer.conditions.length > 0 ? (
        <section className="rounded-card border border-border bg-surface p-5 shadow-card">
          <h2 className="text-section text-ink">Conditions</h2>
          <ul className="mt-2 space-y-1.5">
            {offer.conditions.map((c) => (
              <li key={c} className="flex items-start gap-2 text-sm text-ink-muted">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-warning" />
                {c}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* THE COLLATERAL LOCK. Stated before the accept button, not after. */}
      <div className="rounded-card border border-warning/30 bg-warning/5 p-4 text-sm text-ink-muted">
        <p className="font-medium text-ink">Accepting locks your collateral</p>
        <p className="mt-1">
          The trust registers a security interest and locks the pledged tokens. They cannot be sold
          or pledged again until the loan is discharged. If it defaults, the trust may enforce
          against them.
        </p>
      </div>

      {error ? (
        <p className="rounded-field bg-danger/10 px-3 py-2 text-sm text-danger">{error}</p>
      ) : null}

      <div className="flex gap-3">
        <button
          onClick={decline}
          disabled={!!busy}
          className="flex-1 rounded-field border border-border py-3 text-sm font-medium text-ink transition hover:bg-surface-sunken disabled:opacity-60"
        >
          {busy === 'decline' ? 'Declining...' : 'Decline'}
        </button>
        <button
          onClick={accept}
          disabled={!!busy}
          className="flex-1 rounded-field bg-primary py-3 text-sm font-semibold text-white transition hover:bg-primary-light disabled:opacity-60"
        >
          {busy === 'accept' ? 'Accepting...' : 'Accept offer'}
        </button>
      </div>
      <p className="text-center text-xs text-ink-subtle">
        Preview: accepting records your intent. Disbursement and the collateral lock run when
        lending activates.
      </p>
    </div>
  );
}

function Row({ k, v, strong }: { k: string; v: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2.5">
      <dt className="text-ink-muted">{k}</dt>
      <dd
        className={`tabular text-right ${strong ? 'text-base font-semibold text-ink' : 'font-medium text-ink'}`}
      >
        {v}
      </dd>
    </div>
  );
}

function BackIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 6l-6 6 6 6" />
    </svg>
  );
}
